import { typedEnv } from "../config/typedEnv"

export type PlatformServiceName = "admin" | "billing"

const PLATFORM_SERVICE_PATH_PREFIX: Record<PlatformServiceName, string> = {
  admin: "/api/v1/admin",
  billing: "/api/v1/billing",
}

const PLATFORM_SERVICE_ENV_KEYS: Record<PlatformServiceName, string> = {
  admin: "VITE_PLATFORM_ADMIN_TINTVTO_SERVICE_ENDPOINT",
  billing: "VITE_PLATFORM_BILLING_TINTVTO_SERVICE_ENDPOINT",
}

export const resolveServiceEndpoint = (value: string): string => {
  const trimmed = value.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed.replace(/\/$/, "")

  return `https://${trimmed.replace(/\/$/, "")}`
}

export const resolvePlatformServiceBaseEndpoint = (
  serviceName: PlatformServiceName,
  serviceUrl: string
): string => {
  const normalizedServiceUrl = resolveServiceEndpoint(serviceUrl)
  const servicePathPrefix = PLATFORM_SERVICE_PATH_PREFIX[serviceName]
  const url = new URL(normalizedServiceUrl)

  if (!url.pathname.endsWith(servicePathPrefix))
    throw new Error(
      `Invalid ${serviceName} platform service URL: expected it to end with "${servicePathPrefix}".`
    )

  const basePath = url.pathname.slice(0, -servicePathPrefix.length) || "/"
  url.pathname = basePath
  url.search = ""
  url.hash = ""

  return url.toString().replace(/\/$/, "")
}

const getPlatformServiceUrl = (serviceName: PlatformServiceName): string | undefined => {
  if (serviceName === "admin") return typedEnv.VITE_PLATFORM_ADMIN_TINTVTO_SERVICE_ENDPOINT
  return typedEnv.VITE_PLATFORM_BILLING_TINTVTO_SERVICE_ENDPOINT
}

const requirePlatformServiceUrl = (serviceName: PlatformServiceName): string => {
  const serviceUrl = getPlatformServiceUrl(serviceName)
  if (serviceUrl) return serviceUrl

  throw new Error(
    `Missing ${PLATFORM_SERVICE_ENV_KEYS[serviceName]}. Configure the ${serviceName} platform service endpoint before making ${serviceName} API requests.`
  )
}

export const getPlatformServiceEndpoint = (serviceName: PlatformServiceName): string =>
  resolveServiceEndpoint(requirePlatformServiceUrl(serviceName))

export const validatePlatformServiceEndpoints = () => {
  if (typedEnv.MODE !== "development") return

  const adminEndpoint = resolvePlatformServiceBaseEndpoint(
    "admin",
    requirePlatformServiceUrl("admin")
  )
  const billingEndpoint = resolvePlatformServiceBaseEndpoint(
    "billing",
    requirePlatformServiceUrl("billing")
  )

  if (adminEndpoint === billingEndpoint)
    throw new Error(
      "Invalid platform service configuration: admin and billing resolve to the same base endpoint. Check the deployed admin-test and billing-test ServiceEndpoint outputs, rerender tint-platform/.env.development.local, and rebuild app-dev."
    )
}
